import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { localizeSystemText } from '../../i18n/messages';
import { PAYMENT_WATCHDOG_FOLLOW_UP_STATUS_OPTIONS, PAYMENT_WATCHDOG_PRIORITY_OPTIONS } from './clientSettlementWatchdog';
import { flagsToStatus, statusToFlags, activeSettlementFilters } from './clientSettlementFilters';
import '../manage/contactDetails.scss';

export default function ClientSettlementsFilters({ filters, onApply, onClear }) {
  const { t: tr } = useTranslation();
  const [show, setShow] = useState(false);
  const [draft, setDraft] = useState(filters);
  const dialog = useRef(null);
  const trigger = useRef(null);
  useEffect(() => {
    if (!show) return undefined;
    if (dialog.current.showModal) dialog.current.showModal();
    else dialog.current.setAttribute('open', '');
    return () => { if (trigger.current) trigger.current.focus(); };
  }, [show]);
  const changeWatchdog = (key, value) => setDraft(current => ({ ...current, watchdog: { ...current.watchdog, [key]: value } }));
  const active = activeSettlementFilters(filters, tr);
  const choices = [
    { key: 'showNegativeBalanceOnly', label: tr('billing:underpaidOnly_125'), checked: draft.showNegativeBalanceOnly },
    { key: 'showEmptyClients', label: tr(draft.showEmptyClients ? 'manage:extraHideClientsWithoutEntries_126' : 'manage:extraAlsoShowClientsWithoutEntries_127'), checked: draft.showEmptyClients },
  ];
  return (
    <>
      <div className="col-md-4 mb-3">
        <button ref={trigger} type="button" className="btn manage-button manage-teal-button" aria-haspopup="dialog" onClick={() => { setDraft(filters); setShow(true); }}>
          <i className="fas fa-filter mr-2" aria-hidden="true" />{tr('manage:filters_158')}{active.length ? ` (${active.length})` : ''}
        </button>
      </div>
      {active.length > 0 && (
        <div className="col-12 contact-active-filters mt-2">
          {active.map(filter => (
            <button
              type="button"
              className="contact-filter-chip"
              key={filter.key}
              aria-label={tr('billing:settlementFilters.remove', { label: filter.label })}
              onClick={() => onApply(filter.clear)}
            >
              {filter.label} <span aria-hidden="true">×</span>
            </button>
          ))}
          <button type="button" className="btn btn-link contact-filter-clear" onClick={onClear}>{tr('billing:clearFilters_126')}</button>
        </div>
      )}
      {show && (
        <dialog ref={dialog} className="contact-filter-dialog" onCancel={() => setShow(false)} aria-labelledby="settlement-filters-title">
          <form onSubmit={event => { event.preventDefault(); onApply(draft); setShow(false); }}>
            <div className="modal-header">
              <h4 id="settlement-filters-title" className="modal-title">{tr('manage:filters_158')}</h4>
              <button type="button" className="close" aria-label={tr('manage:closeFilters_165')} onClick={() => setShow(false)}><span aria-hidden="true">×</span></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="settlement-status">{tr('manage:status_146')}</label>
                <select id="settlement-status" className="form-control" value={flagsToStatus(draft)} onChange={event => setDraft({ ...draft, ...statusToFlags(event.target.value) })}>
                  <option value="all">{tr('manage:all_270')}</option>
                  <option value="draft">{tr('billing:draft_123')}</option>
                  <option value="approved">{tr('billing:approved_124')}</option>
                </select>
              </div>
              {choices.map(choice => (
                <button
                  key={choice.key}
                  type="button"
                  className={`btn btn-sm mb-2 mr-2 ${choice.checked ? 'btn-info' : 'btn-outline-secondary'}`}
                  aria-pressed={choice.checked}
                  onClick={() => setDraft({ ...draft, [choice.key]: !choice.checked })}
                >{choice.label}
                </button>
              ))}
              <div className="mt-3 mb-3">
                <button
                  type="button"
                  className={`btn btn-sm ${draft.watchdog.enabledOnly ? 'btn-info' : 'btn-outline-secondary'}`}
                  aria-pressed={draft.watchdog.enabledOnly}
                  onClick={() => changeWatchdog('enabledOnly', !draft.watchdog.enabledOnly)}
                >{tr('billing:overdueOnly_127')}
                </button>
              </div>
              <div className="form-row">
                {[
                  ['minAmount', 'billing:minimumOverdueAmount_128', '0.01'],
                  ['minOverdueDays', 'billing:minimumDaysOverdue_129', '1'],
                ].map(([key, label, step]) => (
                  <div className="form-group col-md-6" key={key}>
                    <label htmlFor={`watchdog-${key}`}>{tr(label)}</label>
                    <input
                      id={`watchdog-${key}`}
                      type="number"
                      min="0"
                      step={step}
                      className="form-control form-control-sm"
                      value={draft.watchdog[key]}
                      onChange={event => changeWatchdog(key, event.target.value)}
                    />
                  </div>
                ))}
              </div>
              {[
                ['priority', 'billing:priority_130', PAYMENT_WATCHDOG_PRIORITY_OPTIONS],
                ['followUpStatus', 'billing:handlingStatus_101', PAYMENT_WATCHDOG_FOLLOW_UP_STATUS_OPTIONS],
              ].map(([key, label, options]) => (
                <div className="form-group" key={key}>
                  <label htmlFor={`watchdog-${key}`}>{tr(label)}</label>
                  <select id={`watchdog-${key}`} className="form-control form-control-sm" value={draft.watchdog[key]} onChange={event => changeWatchdog(key, event.target.value)}>
                    <option value="">{tr('manage:all_270')}</option>
                    {options.map(option => <option key={option.value} value={option.value}>{localizeSystemText(option.text, tr)}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn manage-button manage-teal-button" onClick={() => setShow(false)}>{tr('common:cancel')}</button>
              <button type="submit" className="btn manage-button manage-red-button">{tr('common:apply')}</button>
            </div>
          </form>
        </dialog>
      )}
    </>
  );
}
ClientSettlementsFilters.propTypes = {
  filters: PropTypes.shape({
    showDraft: PropTypes.bool.isRequired,
    showApproved: PropTypes.bool.isRequired,
    showNegativeBalanceOnly: PropTypes.bool.isRequired,
    showEmptyClients: PropTypes.bool.isRequired,
    watchdog: PropTypes.object.isRequired,
  }).isRequired,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};
